import * as _ from 'lodash-es';
import type { IframeHTMLAttributes, ReactNode } from 'react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import ignoreInChromatic from '../../utils/ignoreInChromatic';
import findClosestScrollingContainer from '../utils/findClosestScrollingContainer';

function usePreventScrollJump({
  iframeRef,
  isEnabled,
}: {
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  isEnabled: boolean;
}) {
  useEffect(() => {
    if (!isEnabled || !iframeRef.current) return undefined;
    const scrollingContainer = findClosestScrollingContainer(
      iframeRef.current.parentElement,
    );
    if (!scrollingContainer) return undefined;

    let prevTop = 0;
    let isIframeFocused = false;
    let didDetectInitialScroll = false;
    const handleScroll = () => {
      if (isIframeFocused && scrollingContainer.scrollTop === 0) {
        scrollingContainer.scrollTop = prevTop;
      }

      const hasScrolledUp =
        scrollingContainer.scrollTop < prevTop &&
        scrollingContainer.scrollTop < SCROLL_OFFSET;

      if (!didDetectInitialScroll && hasScrolledUp) {
        scrollingContainer.scrollTop = prevTop;
        didDetectInitialScroll = true;
      }

      prevTop = scrollingContainer.scrollTop;
    };

    const handleBlur = () => {
      isIframeFocused =
        document.activeElement instanceof HTMLIFrameElement &&
        document.activeElement.name === iframeRef.current?.name;
      prevTop = scrollingContainer.scrollTop;
    };

    const handleFocus = () => {
      isIframeFocused = false;
    };

    window.addEventListener('blur', handleBlur, true);
    window.addEventListener('focus', handleFocus, true);
    scrollingContainer.addEventListener('scroll', handleScroll, false);

    return () => {
      window.removeEventListener('blur', handleBlur, true);
      window.removeEventListener('focus', handleFocus, true);
      scrollingContainer.removeEventListener('scroll', handleScroll, false);
    };
  }, [isEnabled, iframeRef]);
}

function usePreventFocusStealing({
  iframeRef,
  isEnabled,
  isSelected,
}: {
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  isEnabled: boolean;
  isSelected?: boolean;
}) {
  const activeElementRef = useRef<EventTarget | null>(null);

  useLayoutEffect(() => {
    if (!isEnabled || !iframeRef.current) return undefined;
    const scrollingContainer = findClosestScrollingContainer(
      iframeRef.current.parentElement,
    );
    if (!scrollingContainer) return undefined;

    activeElementRef.current = document.activeElement;

    const handleCurrentFrameFocus = (e: FocusEvent) => {
      activeElementRef.current = e.target;
    };

    // The blur listener is called also when the iframe is focused.
    // Need to return the focus back to the previously focused element.
    const handleBlur = () => {
      _.defer(() => {
        if (
          !isSelected &&
          document.activeElement instanceof HTMLIFrameElement &&
          document.activeElement.name === iframeRef.current?.name &&
          activeElementRef.current instanceof HTMLElement
        ) {
          // Can't focus the body, so blurring the iframe instead.
          if (activeElementRef.current.tagName === 'BODY') {
            iframeRef.current.blur();
          } else {
            activeElementRef.current.focus();
          }
        }
      });
    };

    window.addEventListener('focus', handleCurrentFrameFocus, true);
    window.addEventListener('blur', handleBlur, true);

    return () => {
      window.removeEventListener('focus', handleCurrentFrameFocus, true);
      window.removeEventListener('blur', handleBlur, true);
    };
  }, [isEnabled, iframeRef, isSelected]);
}

interface IframeWithFallbackProps
  extends IframeHTMLAttributes<HTMLIFrameElement> {
  type: string;
  fallback: ReactNode;
  selected?: boolean;
}

const EMBEDS_WITH_SIDE_EFFECTS = new Set(['google-sheet', 'coda']);
const SCROLL_OFFSET = 20;

const usePreventIframeSideEffects = (type: string, isSelected?: boolean) => {
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const isEnabled = !readOnly && EMBEDS_WITH_SIDE_EFFECTS.has(type);

  usePreventScrollJump({ iframeRef, isEnabled });
  usePreventFocusStealing({ iframeRef, isEnabled, isSelected });

  return { iframeRef };
};

const IframeWithFallback = ({
  fallback,
  type,
  selected,
  ...props
}: IframeWithFallbackProps) => {
  const nameRef = useRef<string | null>(null);
  const { iframeRef } = usePreventIframeSideEffects(type, selected);

  const getIframeName = () => {
    if (nameRef.current) {
      return nameRef.current;
    }
    const name = _.uniqueId('iframe_with_fallback');
    nameRef.current = name;
    return name;
  };

  const [isShowingFallback, setIsShowingFallback] = useState(false);

  useEffect(() => {
    const removeListener = window.desktop?.onIframeBlock?.(
      getIframeName(),
      () => {
        setIsShowingFallback(true);
      },
    );
    return removeListener;
  }, []);

  return isShowingFallback ? (
    <>{fallback}</>
  ) : (
    // eslint-disable-next-line jsx-a11y/iframe-has-title
    <iframe
      {...ignoreInChromatic()}
      ref={iframeRef}
      name={getIframeName()}
      {...props}
    />
  );
};

export default IframeWithFallback;
